import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  approveIssuedInvoice,
  cancelIssuedInvoice,
  createIssuedInvoice,
  createIssuedInvoiceStorno,
  duplicateIssuedInvoice,
  getIssuedInvoice,
  listCountyOptions,
  listInvoiceSeries,
  upsertInvoiceSeries,
  type InvoiceSeriesInput,
  listIssuedInvoices,
  listUnitOptions,
  refreshIssuedInvoiceState,
  setIssuedInvoiceProject,
  updateIssuedInvoice,
  validateIssuedInvoice,
  type IssuedInvoiceFilters,
  type IssuedInvoiceInput,
} from '../lib/issuedInvoicesApi';
import { useActiveCompanyId } from '../store/useCompanyStore';

export const ISSUED_INVOICES_KEY = ['issued-invoices'] as const;

export function useIssuedInvoices(filters: IssuedInvoiceFilters = {}) {
  const activeCompanyId = useActiveCompanyId() ?? undefined;
  const merged: IssuedInvoiceFilters = { companyId: activeCompanyId, ...filters };
  return useQuery({
    queryKey: [...ISSUED_INVOICES_KEY, 'list', merged],
    queryFn: () => listIssuedInvoices(merged),
    placeholderData: (prev) => prev,
  });
}

/** One invoice; polls every 8 s while ANAF has not answered yet. */
export function useIssuedInvoice(id: string | null) {
  return useQuery({
    queryKey: [...ISSUED_INVOICES_KEY, 'one', id],
    queryFn: () => getIssuedInvoice(id as string),
    enabled: Boolean(id),
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s === 'SENT' || s === 'SENDING' ? 8_000 : false;
    },
  });
}

export function useInvoiceSeries(companyId?: string) {
  const activeCompanyId = useActiveCompanyId() ?? undefined;
  const effective = companyId ?? activeCompanyId;
  return useQuery({
    queryKey: [...ISSUED_INVOICES_KEY, 'series', effective ?? null],
    queryFn: () => listInvoiceSeries(effective),
    staleTime: 60_000,
  });
}

export function useUpsertInvoiceSeries() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: InvoiceSeriesInput) => upsertInvoiceSeries(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...ISSUED_INVOICES_KEY, 'series'] });
    },
  });
}

export function useUnitOptions() {
  return useQuery({
    queryKey: [...ISSUED_INVOICES_KEY, 'units'],
    queryFn: listUnitOptions,
    staleTime: Infinity,
  });
}

export function useCountyOptions() {
  return useQuery({
    queryKey: [...ISSUED_INVOICES_KEY, 'counties'],
    queryFn: listCountyOptions,
    staleTime: Infinity,
  });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ISSUED_INVOICES_KEY });
    void qc.invalidateQueries({ queryKey: ['invoices'] });
    void qc.invalidateQueries({ queryKey: ['clients'] });
  };
}

export function useCreateIssuedInvoice() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: IssuedInvoiceInput) => createIssuedInvoice(input),
    onSuccess: invalidate,
  });
}

export function useUpdateIssuedInvoice() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: IssuedInvoiceInput }) =>
      updateIssuedInvoice(id, input),
    onSuccess: invalidate,
  });
}

export function useValidateIssuedInvoice() {
  return useMutation({ mutationFn: (id: string) => validateIssuedInvoice(id) });
}

export function useApproveIssuedInvoice() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => approveIssuedInvoice(id),
    onSettled: invalidate,
  });
}

export function useRefreshIssuedInvoiceState() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => refreshIssuedInvoiceState(id),
    onSuccess: invalidate,
  });
}

export function useCancelIssuedInvoice() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => cancelIssuedInvoice(id),
    onSuccess: invalidate,
  });
}

/** Re-tag an invoice with a project (internal tag, works on sent invoices too). */
export function useSetIssuedInvoiceProject() {
  const invalidate = useInvalidate();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, projectId }: { id: string; projectId: string | null }) =>
      setIssuedInvoiceProject(id, projectId),
    onSuccess: (invoice) => {
      invalidate();
      qc.setQueryData([...ISSUED_INVOICES_KEY, 'one', invoice.id], invoice);
      // The project's Financiar tab lists the invoice; both the old and the new one move.
      void qc.invalidateQueries({ queryKey: ['project'] });
      void qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useDuplicateIssuedInvoice() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => duplicateIssuedInvoice(id),
    onSuccess: invalidate,
  });
}

/** „Emite storno”: creates the reversing draft and validates it at ANAF. */
export function useCreateIssuedInvoiceStorno() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => createIssuedInvoiceStorno(id),
    onSuccess: invalidate,
  });
}
